import { trpc } from "../../lib/trpc.js";
import { Girando, Vazio, cx } from "../../components/ui.js";

/**
 * Grade de escolha das bibliotecas — referências e capas.
 *
 * Duas colunas no celular. Alvos grandes: o operador toca com o polegar, em pé,
 * às vezes com a mão trêmula de quem está apressado.
 */
export function GradeSelecao({
  itens,
  selecionados,
  aoAlternar,
  vazio,
  carregando,
  proporcao = "square",
}: {
  itens: { id: number; url: string; label?: string | null }[];
  selecionados: number[];
  aoAlternar: (id: number) => void;
  vazio: { titulo: string; descricao: string };
  carregando: boolean;
  proporcao?: "square" | "cover";
}) {
  if (carregando) {
    return (
      <div className="flex justify-center py-10">
        <Girando className="size-6 text-texto-fraco" />
      </div>
    );
  }

  if (itens.length === 0) {
    return <Vazio titulo={vazio.titulo} descricao={vazio.descricao} />;
  }

  return (
    <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {itens.map((item) => {
        const ativo = selecionados.includes(item.id);
        const ordem = selecionados.indexOf(item.id) + 1;

        return (
          <li key={item.id}>
            <button
              onClick={() => aoAlternar(item.id)}
              aria-pressed={ativo}
              className={cx(
                "relative w-full overflow-hidden rounded-peca border-2 transition-colors",
                proporcao === "square" ? "aspect-square" : "aspect-[9/16]",
                ativo ? "border-brasa" : "border-transparent hover:border-borda-forte",
              )}
            >
              <img src={item.url} alt={item.label ?? ""} className="size-full object-cover" />

              {ativo && (
                <span
                  className={cx(
                    "absolute right-1.5 top-1.5 flex size-6 items-center justify-center",
                    "num rounded-full bg-brasa text-[0.8125rem] font-semibold text-fundo",
                  )}
                >
                  {ordem}
                </span>
              )}

              {item.label && (
                <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/85 to-transparent px-2 pb-1.5 pt-4 text-left text-[0.75rem] text-texto">
                  {item.label}
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export function EtapaAuxiliares({
  selecionados,
  aoMudar,
}: {
  selecionados: string[];
  aoMudar: (paths: string[]) => void;
}) {
  const banco = trpc.imageBank.list.useQuery();
  const itens = banco.data ?? [];

  const idsSelecionados = itens.filter((i) => selecionados.includes(i.filePath)).map((i) => i.id);

  function alternar(id: number) {
    const item = itens.find((i) => i.id === id);
    if (!item) return;

    const jaTem = selecionados.includes(item.filePath);
    if (jaTem) {
      aoMudar(selecionados.filter((p) => p !== item.filePath));
      return;
    }

    // Quatro é o limite de slots da geração; avisar seria pior que ignorar em
    // silêncio, porque o operador vê o número na miniatura.
    if (selecionados.length >= 4) return;
    aoMudar([...selecionados, item.filePath]);
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[0.9375rem] leading-relaxed text-texto-suave">
        Até quatro imagens que orientam o modelo — cenário, estilo, objetos. A ordem em que você
        toca é a ordem em que elas são enviadas.
      </p>

      <GradeSelecao
        itens={itens.map((i) => ({ id: i.id, url: i.url, label: i.label }))}
        selecionados={idsSelecionados}
        aoAlternar={alternar}
        carregando={banco.isLoading}
        vazio={{
          titulo: "O banco de imagens está vazio",
          descricao: "Suba imagens em Acervo → Banco de imagens para usá-las como referência.",
        }}
      />
    </div>
  );
}

export function EtapaCapa({
  capaId,
  aoEscolher,
}: {
  capaId: number | null;
  aoEscolher: (id: number | null) => void;
}) {
  const capas = trpc.bookCovers.list.useQuery();
  const itens = capas.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[0.9375rem] leading-relaxed text-texto-suave">
        A capa ocupa os 40% de cima da peça final. Sem capa, a foto é entregue sozinha.
      </p>

      <GradeSelecao
        itens={itens.map((c) => ({ id: c.id, url: c.url, label: c.label }))}
        selecionados={capaId ? [capaId] : []}
        aoAlternar={(id) => aoEscolher(capaId === id ? null : id)}
        carregando={capas.isLoading}
        proporcao="cover"
        vazio={{
          titulo: "Nenhuma capa cadastrada",
          descricao: "Suba capas em Acervo → Capas de livro para compor com as fotos.",
        }}
      />
    </div>
  );
}
